import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

interface PhoneChangeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (phoneNumber: string) => Promise<void>;
  currentPhone?: string;
}

export function PhoneChangeModal({
  open,
  onClose,
  onSubmit,
  currentPhone = "",
}: PhoneChangeModalProps) {
  const [phoneNumber, setPhoneNumber] = useState<string>(currentPhone);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>("");

  const isValidPhoneNumber = (phone: string): boolean => {
    // Basic phone number validation: 7-15 digits, allowing + at start
    const phoneRegex = /^\+?[0-9]{7,15}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove any non-numeric characters except plus sign
    const cleanedValue = e.target.value.replace(/[^0-9]/g, "");
    setPhoneNumber(cleanedValue);
    setError(""); // Clear error when user types
  };

  const handleSubmit = async () => {
    if (!phoneNumber) return;

    if (!isValidPhoneNumber(`+${phoneNumber}`)) {
      setError("Please enter a valid phone number (7-15 digits)");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(`+${phoneNumber}`);
      onClose();
    } catch (error) {
      console.error("Error updating phone number:", error);
      setError("Failed to update phone number. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPhoneNumber(currentPhone);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Phone Number</DialogTitle>
          <DialogDescription>
            Enter the new phone number below. Please include the country code.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                +
              </span>
              <Input
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="Enter your phone number"
                disabled={isSubmitting}
                className={`pl-6 ${error ? "border-red-500" : ""}`}
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-4">
          <p className="text-sm text-yellow-600 dark:text-yellow-500">
            Warning: Updating the user&apos;s phone number will log them out of
            all current sessions.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!phoneNumber || isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
